import React, { useState, useEffect, useCallback } from 'react'
import {
  Typography,
  Paper,
  Grid,
  Box,
  Divider,
  Chip,
  Button,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import moment from 'moment'
import TableComponent from 'components/shared/TableComponent'
import SimpleDropDownPicker from 'components/forms/Dropdown/SimpleDropDownPicker'
import Loader from 'components/shared/Loader'
import { transactionTypeOptions } from 'constants/constantVariables'
import request from "../../../../../tools/request";
import emptyIcon from 'styles/images/icons/empty.svg'

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
  },
  card: {
    padding: theme.spacing(2),
    height: '100%',
    borderRadius: theme.spacing(1),
    border: '1px solid var(--color-black-10, #0000001A)',
    boxShadow: 'none',
  },
  cardTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  fieldLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  fieldValue: {
    marginBottom: theme.spacing(1.5),
  },
  positiveAmount: {
    color: theme.palette.success.main,
    fontWeight: 500,
  },
  negativeAmount: {
    color: theme.palette.error.main,
    fontWeight: 500,
  },
  revenueAmount: {
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  rightAlign: {
    textAlign: 'right',
  },
  avatarContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  bankAccountsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  transactionChip: {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.secondary,
  },
  badgeRed: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    borderRadius: 20,
    padding: '4px 12px',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  badgeBlue: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: 20,
    padding: '4px 12px',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  detectionCard: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    border: '1px solid var(--color-black-10, #0000001A)',
    boxShadow: 'none',
    height: '100%',
  },
  insightCard: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    border: '1px solid var(--color-black-10, #0000001A)',
    boxShadow: 'none',
    marginBottom: theme.spacing(2),
  },
  insightTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(0.5),
  },
  insightSubtitle: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  sectionSpacing: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(3),
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  filterControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    '& > *': {
      minWidth: 150,
    }
  },
  clearButton: {
    borderColor: theme.palette.grey[400],
    color: theme.palette.text.primary,
  },
  accountChip: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    fontWeight: 500,
    fontSize: '0.75rem',
    maxWidth: '100%',
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      fontSize: '0.75rem',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }
  },
  typeDebit: {
    backgroundColor: theme.palette.grey[600],
    color: theme.palette.common.white,
    fontSize: '0.75rem',
  },
  typeCredit: {
    backgroundColor: 'black',
    color: 'white',
    fontSize: '0.75rem',
  },
  categoryPayroll: {
    backgroundColor: 'var(--badge-review-bg)',
    color: '#0D47A1',
  },
  categoryWireTransfer: {
    backgroundColor: '#F3E5F5',
    color: '#7B1FA2',
  },
  categoryCreditCardPayments: {
    backgroundColor: 'var(--badge-declined-bg)',
    color: 'var(--status-declined)',
  },
  categoryLoanDebits: {
    backgroundColor: '#FFF8E1',
    color: '#FF8F00',
  },
  categoryRent: {
    backgroundColor: 'var(--badge-approved-bg)',
    color: 'var(--status-approved)',
  },
  categoryOverdrafts: {
    backgroundColor: '#FFCDD2',
    color: '#B71C1C',
  },
  categoryGambling: {
    backgroundColor: '#FFECB3',
    color: '#FF6F00',
  },
  categoryMCA: {
    backgroundColor: '#C8E6C9',
    color: '#1B5E20',
  },
  categoryFactor: {
    backgroundColor: '#CFD8DC',
    color: '#37474F',
  },
  categoryDefault: {
    backgroundColor: 'var(--badge-approved-bg)',
    color: '#016630',
  },
  flagChip: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    fontSize: '0.75rem',
    margin: theme.spacing(0.25),
  },
  transactionAmount: {
    fontWeight: 600,
  },
  transactionsTableContainer: {
    overflowX: 'auto',
    '& .react-bootstrap-table table': {
      minWidth: '800px',
    }
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    textAlign: 'center',
    '& img': {
      width: '80px',
      height: '80px',
      marginBottom: theme.spacing(2),
      opacity: 0.5,
    },
    '& p': {
      color: theme.palette.text.secondary,
      fontSize: '1rem',
      margin: 0,
    }
  },
}))

const Transactions = ({ applicantUUID, assetsReportData, assetsReportTransactions, assetsReportTransactionsHeaders, getAssetsReportData, getAssetsReportTransactions, resetAssetsReportData }) => {
  const classes = useStyles()

  const [loading, setLoading] = useState(false)
  const [transactionsLoading, setTransactionsLoading] = useState(false)
  const [pagination, setPagination] = useState('')
  const [filters, setFilters] = useState({
    type: 'All',
    category: 'All Categories',
    accountName: 'All Accounts'
  })

    const totalRecords = assetsReportTransactionsHeaders['x-pagination-total-records']
    const totalPages = assetsReportTransactionsHeaders['x-pagination-total-pages']

  // Reset assets report data when component mounts
  useEffect(() => {
    resetAssetsReportData()
  }, [resetAssetsReportData])

  useEffect(() => {
    if (applicantUUID) {
      setLoading(true)
      getAssetsReportData(applicantUUID).finally(() => {
        setLoading(false)
      })
    }
  }, [applicantUUID, getAssetsReportData])

  const getTransactions = useCallback((pagination = {}, filterParams = {}) => {
    if (applicantUUID) {
      setTransactionsLoading(true)
      const params = {
        type: filterParams.type !== 'All' ? filterParams.type?.toUpperCase() : undefined,
        accountName: filterParams.accountName !== 'All Accounts' ? filterParams.accountName : undefined,
        categoryName: filterParams.category !== 'All Categories' ? filterParams.category : undefined,
      }
      // Remove undefined values
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key])

      getAssetsReportTransactions(applicantUUID, params, pagination).finally(() => {
        setTransactionsLoading(false)
      })
    }
  }, [applicantUUID, getAssetsReportTransactions])

  useEffect(() => {
    getTransactions(pagination, filters)
  }, [getTransactions, pagination])

  const transactionData = assetsReportData || []

  const allTransactions = assetsReportTransactions || []

  const [accountOptions, setAccountOptions] = useState([
    { label: 'All Accounts', value: 'All Accounts' }
  ]);

  const [categoryOptions, setCategoryOptions] = useState([
    {label: 'All Categories', value: 'All Categories'},
  ]);

  useEffect(() => {
    if (applicantUUID) {
      request(`applications/${applicantUUID}/assets-report/filters`, 'GET', null, false)
          .then(res => {
            const uniqueAccounts = [...new Set(res.accountNames || [])];
            const uniqueCategories = [...new Set(res.categories || [])];
            const newCategoryOptions = [
              { label: 'All Categories', value: 'All Categories' },
              ...uniqueCategories.map(category => ({
                label: category.label,
                value: category.value
              }))
            ];
            setCategoryOptions(newCategoryOptions);
            const newAccountOptions = [
              { label: 'All Accounts', value: 'All Accounts' },
              ...uniqueAccounts.map(accountName => ({
                label: accountName,
                value: accountName
              }))
            ];
            setAccountOptions(newAccountOptions);
          })
          .catch(err => {
            console.error('Error fetching account names:', err);
          });
    }
  }, [applicantUUID]);

  const getFlags = (amount, description) => {
    const flags = []
    if (Math.abs(amount) > 1000) flags.push('Large')
    if (amount < 0 && Math.abs(amount) > 100) flags.push('Overdraft')
    if (description?.toLowerCase().includes('nsf')) flags.push('NSF')
    if (description?.toLowerCase().includes('recurring')) flags.push('Recurring')
    if (description?.toLowerCase().includes('returned')) flags.push('Returned')
    return flags
  }

  const formatAmount = (amount) => {
    const prefix = amount >= 0 ? '+' : '-'
    const value = Math.abs(amount)
    return `${prefix}${formatCurrency(value)}`
  }

  const getCategoryChipClass = (category) => {
    switch (category) {
      case 'Payroll': return classes.categoryPayroll
      case 'Wire Transfer': return classes.categoryWireTransfer
      case 'ACH': return classes.categoryACH
      case 'Credit Card Payments': return classes.categoryCreditCardPayments
      case 'Loan Debits': return classes.categoryLoanDebits
      case 'Rent': return classes.categoryRent
      case 'Utilities': return classes.categoryUtilities
      case 'Overdrafts': return classes.categoryOverdrafts
      case 'Gambling': return classes.categoryGambling
      case 'MCA': return classes.categoryMCA
      case 'Factor': return classes.categoryFactor
      default: return classes.categoryDefault
    }
  }

  const handleTransactionTypeChange = (value) => {
    const newFilters = { ...filters, type: value }
    setFilters(newFilters)
    setPagination('')
    getTransactions({ page: 0, pageSize: pagination.pageSize }, newFilters)
  }

  const handleCategoryChange = (value) => {
    const newFilters = { ...filters, category: value }
    setFilters(newFilters)
    setPagination('')
    getTransactions({ page: 0, pageSize: pagination.pageSize }, newFilters)
  }

  const handleAccountChange = (value) => {
    const newFilters = { ...filters, accountName: value }
    setFilters(newFilters)
    setPagination('')
    getTransactions({ page: 0, pageSize: pagination.pageSize }, newFilters)
  }

  const clearFilters = () => {
    const clearedFilters = {
      type: 'All',
      category: 'All Categories',
      accountName: 'All Accounts'
    }
    setFilters(clearedFilters)
    setPagination('')
    getTransactions({ page: 0, pageSize: pagination.pageSize }, clearedFilters)
  }

  const transactionColumns = [
    {
      dataField: 'date',
      text: 'Date',
      sort: true,
      headerStyle: { width: '10%' },
      formatter: (cell) => new Date(cell).toLocaleDateString()
    },
    {
      dataField: 'accountName',
      text: 'Account',
      sort: true,
      headerStyle: { width: '18%', minWidth: '150px' },
      style: { whiteSpace: 'nowrap', overflow: 'visible' },
      formatter: (cell) => {
        const accountDisplay = cell.replace(' ****', ' ••••')
        return (
          <Chip
            label={accountDisplay}
            size="small"
            className={classes.accountChip}
            title={cell}
          />
        )
      }
    },
    {
      dataField: 'originalDescription',
      text: 'Description',
      sort: true,
      headerStyle: { width: '22%' }
    },
    {
      dataField: 'amount',
      text: 'Amount',
      sort: true,
      headerStyle: { width: '12%', textAlign: 'right' },
      style: { textAlign: 'right' },
      formatter: (cell) => (
        <span>
          {formatAmount(cell)}
        </span>
      )
    },
    {
      dataField: 'direction',
      text: 'Type',
      sort: true,
      headerStyle: { width: '8%' },
      formatter: (cell) => (
        <Chip
          label={cell}
          size="small"
          className={cell === 'Debit' ? classes.typeDebit : classes.typeCredit}
        />
      )
    },
    {
      dataField: 'category',
      text: 'Category',
      sort: true,
      headerStyle: { width: '15%' },
      formatter: (cell, row) => {
        const category = row?.roxCategory ? row.roxCategory : "Other"
        return (
          <Chip
            label={category}
            size="small"
            className={getCategoryChipClass(category)}
          />
        )
      }
    },
    {
      dataField: 'flags',
      text: 'Flags',
      sort: false,
      hidden: true,
      headerStyle: { width: '15%' },
      formatter: (cell, row) => {
        const flags = getFlags(row.amount, row.originalDescription)
        return (
          <Box>
            {flags.map((flag, index) => (
              <Chip
                key={index}
                label={flag}
                size="small"
                className={classes.flagChip}
              />
            ))}
          </Box>
        )
      }
    }
  ]

  const detectionData = assetsReportData?.financialInstabilityInsights || []

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    return moment(dateString).format('DD/MM/YY')
  }

  const getAmountClass = (amount, type = 'balance') => {
    return amount >= 0 ? classes.positiveAmount : classes.negativeAmount
  }

  const bankAccountColumns = [
    {
      dataField: 'institution',
      text: 'Institution',
      sort: true,
      headerStyle: { width: '25%' }
    },
    {
      dataField: 'accountNumber',
      text: 'Account Number',
      sort: true,
      headerStyle: { width: '20%' }
    },
    {
      dataField: 'currentBalance',
      text: 'Current Balance',
      sort: true,
      headerStyle: { width: '20%', textAlign: 'right' },
      style: { textAlign: 'right' },
      formatter: (cell) => (
        <span className={getAmountClass(cell)}>
          {formatCurrency(cell)}
        </span>
      )
    },
    {
      dataField: 'availableBalance',
      text: 'Available Balance',
      sort: true,
      headerStyle: { width: '20%', textAlign: 'right' },
      style: { textAlign: 'right' },
      formatter: (cell) => (
        <span className={getAmountClass(cell)}>
          {formatCurrency(cell)}
        </span>
      )
    },
    {
      dataField: 'revenue',
      text: 'Revenue',
      sort: true,
      headerStyle: { width: '15%', textAlign: 'right' },
      style: { textAlign: 'right' },
      formatter: (cell) => (
        <span className={getAmountClass(cell, 'revenue')}>
          {formatCurrency(cell)}
        </span>
      )
    }
  ]

  return (
    <Box className={classes.root}>
      {loading ? (
        <Loader />
      ) : (
        <>
          <Typography variant="h6" className={classes.sectionTitle}>
            Bank Transactions
          </Typography>
          <Divider style={{ marginBottom: 24 }} />

      <Grid container spacing={3} style={{ marginBottom: 32 }}>
        <Grid item xs={12} md={4}>
          <Paper className={classes.card}>
            <Typography variant="h6" className={classes.cardTitle}>
              Account Holder
            </Typography>
            <Box>
              <Typography className={classes.fieldLabel}>Name</Typography>
              <Typography className={classes.fieldValue}>
                {transactionData?.accountHolder?.name || 'N/A'}
              </Typography>

              <Typography className={classes.fieldLabel}>Address</Typography>
              <Typography className={classes.fieldValue}>
                {transactionData?.accountHolder?.address || 'N/A'}
              </Typography>

              <Typography className={classes.fieldLabel}>Phone</Typography>
              <Typography className={classes.fieldValue}>
                {transactionData?.accountHolder?.phone || 'N/A'}
              </Typography>

              <Typography className={classes.fieldLabel}>Email</Typography>
              <Typography className={classes.fieldValue}>
                {transactionData?.accountHolder?.email || 'N/A'}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper className={classes.card}>
            <Typography variant="h6" className={classes.cardTitle}>
              Account Summary
            </Typography>
            <Box>
              <Typography className={classes.fieldLabel}>Total Accounts</Typography>
              <Typography className={classes.fieldValue}>
                {transactionData?.accountsSummary?.totalAccounts || 0}
              </Typography>

              <Typography className={classes.fieldLabel}>Total Current Balance</Typography>
              <Typography className={`${classes.fieldValue} ${getAmountClass(transactionData?.accountsSummary?.totalCurrentBalance || 0)}`}>
                {formatCurrency(transactionData?.accountsSummary?.totalCurrentBalance || 0)}
              </Typography>

              <Typography className={classes.fieldLabel}>Total Available Balance</Typography>
              <Typography className={`${classes.fieldValue} ${getAmountClass(transactionData?.accountsSummary?.totalAvailableBalance || 0)}`}>
                {formatCurrency(transactionData?.accountsSummary?.totalAvailableBalance || 0)}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper className={classes.card}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box flex={1}>
                <Typography variant="h6" className={classes.cardTitle}>
                  Financial Overview
                </Typography>
                <Box>
                  <Typography className={classes.fieldLabel}>Avg Monthly Revenue</Typography>
                  <Typography className={`${classes.fieldValue} ${getAmountClass(transactionData?.financialOverview?.avgMonthlyRevenue || 0, 'revenue')}`}>
                    {formatCurrency(transactionData?.financialOverview?.avgMonthlyRevenue || 0)}
                  </Typography>

                  <Typography className={classes.fieldLabel}>True Revenue</Typography>
                  <Typography className={`${classes.fieldValue} ${getAmountClass(transactionData?.financialOverview?.trueRevenue || 0, 'revenue')}`}>
                    {formatCurrency(transactionData?.financialOverview?.trueRevenue || 0)}
                  </Typography>

                  <Typography className={classes.fieldLabel}>Total Expenses</Typography>
                  <Typography className={`${classes.fieldValue} ${getAmountClass(transactionData?.financialOverview?.totalExpenses || 0, 'expense')}`}>
                    {formatCurrency(transactionData?.financialOverview?.totalExpenses || 0)}
                  </Typography>

                  <Typography className={classes.fieldLabel}>Days Negative</Typography>
                  <Typography className={`${classes.fieldValue} ${classes.negativeAmount}`}>
                    {transactionData?.financialOverview?.daysNegative || 0} days
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

        <Typography variant="h6" className={classes.cardTitle}>
            Bank Accounts
          </Typography>
      <Paper className={classes.card}>
        <Box className={classes.bankAccountsHeader}>
        <Typography variant="subtitle1" style={{ marginBottom: 16, fontWeight: 500 }}>
          {'Business Checking'}
        </Typography>
          <Chip
            label={`${transactionData?.businessChecking?.length || 0} txns`}
            className={classes.transactionChip}
            size="small"
          />
        </Box>

        <TableComponent
          data={transactionData?.businessChecking || []}
          columns={bankAccountColumns}
          keyField="institution"
          showPagination={false}
          showLimitOptions={false}
          className="bank-accounts-table"
        />
      </Paper>

      <Box className={classes.sectionSpacing}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper className={classes.detectionCard}>
              <Typography variant="h6" className={classes.cardTitle}>
                MCA Detection
              </Typography>
              <Box display="flex" alignItems="center" style={{ marginBottom: 16 }}>
                <Typography variant="body2" style={{ marginRight: 8 }}>
                  MCA Withhold Rate
                </Typography>
                {/* <span className={classes.badgeRed}>
                  {assetsReportData?.mcaAccountDetections?.withholdRate || 'N/A'}
                </span> */}
              </Box>
              <Typography variant="subtitle2" style={{ marginBottom: 12, fontWeight: 500 }}>
                Detected Transactions:
              </Typography>
              <TableComponent
                data={assetsReportData?.mcaAccountDetections || []}
                columns={[
                  {
                    dataField: 'date',
                    text: 'Date',
                    sort: true,
                    headerStyle: { width: '30%' },
                    formatter: (cell) => formatDate(cell)
                  },
                  {
                    dataField: 'description',
                    text: 'Description',
                    sort: true,
                    headerStyle: { width: '45%' }
                  },
                  {
                    dataField: 'amount',
                    text: 'Amount',
                    sort: true,
                    headerStyle: { width: '25%', textAlign: 'right' },
                    style: { textAlign: 'right' },
                    formatter: (cell) => formatCurrency(cell)
                  }
                ]}
                keyField="date"
                showPagination={false}
                showLimitOptions={false}
                className="mca-detection-table"
                noDataIndication="No MCA transactions detected"
              />
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper className={classes.detectionCard}>
              <Typography variant="h6" className={classes.cardTitle}>
                Factor Detection
              </Typography>
              <Box display="flex" alignItems="center" style={{ marginBottom: 16 }}>
                <Typography variant="body2" style={{ marginRight: 8 }}>
                  Factoring Revenue
                </Typography>
                {/* <span className={classes.badgeBlue}>
                  {assetsReportData?.factoringDetections?.factoringRevenue || 'N/A'}
                </span> */}
              </Box>
              <Typography variant="subtitle2" style={{ marginBottom: 12, fontWeight: 500 }}>
                Detected Transactions:
              </Typography>
              <TableComponent
                data={assetsReportData?.factoringDetections || []}
                columns={[
                  {
                    dataField: 'date',
                    text: 'Date',
                    sort: true,
                    headerStyle: { width: '30%' },
                    formatter: (cell) => formatDate(cell)
                  },
                  {
                    dataField: 'description',
                    text: 'Description',
                    sort: true,
                    headerStyle: { width: '45%' }
                  },
                  {
                    dataField: 'amount',
                    text: 'Amount',
                    sort: true,
                    headerStyle: { width: '25%', textAlign: 'right' },
                    style: { textAlign: 'right' },
                    formatter: (cell) => formatCurrency(cell)
                  }
                ]}
                keyField="date"
                showPagination={false}
                showLimitOptions={false}
                className="factor-detection-table"
                noDataIndication="No factoring transactions detected"
              />
            </Paper>
          </Grid>
        </Grid>
      </Box>

          <Box className={classes.sectionSpacing}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Financial Instability Insights
            </Typography>
            <Divider style={{marginBottom: 24}}/>

            {detectionData && detectionData.length > 0 ? (
              detectionData.map((insight, index) => (
                <Paper key={index} className={classes.insightCard}>
                  <Typography className={classes.insightTitle}>
                    {insight.insight}
                  </Typography>
                  <Typography className={classes.insightSubtitle}>
                    {insight.description}
                  </Typography>
                </Paper>
              ))
            ) : (
              <Paper className={classes.card}>
                <div className={classes.emptyState}>
                  <img src={emptyIcon} alt='empty' />
                  <p>No Financial Instability Insights Available</p>
                </div>
              </Paper>
            )}
          </Box>

      <Box className={classes.sectionSpacing}>
        <Paper className={classes.card}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Transactions
          </Typography>

          <Box className={classes.filterRow}>
            <Box>
              <Typography variant="h6" style={{ marginBottom: 8 }}>
                All Transactions ({allTransactions?.length || 0})
              </Typography>
              <Box className={classes.filterControls}>
                <SimpleDropDownPicker
                  options={transactionTypeOptions}
                  label="Transaction Type"
                  selectedValue={filters.type}
                  setSelectedValue={handleTransactionTypeChange}
                  labelField="label"
                  valueField="value"
                  fullWidth={false}
                />

                <SimpleDropDownPicker
                  options={categoryOptions}
                  label="Category"
                  selectedValue={filters.category}
                  setSelectedValue={handleCategoryChange}
                  labelField="label"
                  valueField="value"
                  fullWidth={false}
                />

                <SimpleDropDownPicker
                  options={accountOptions}
                  label="Account"
                  selectedValue={filters.accountName}
                  setSelectedValue={handleAccountChange}
                  labelField="label"
                  valueField="value"
                  fullWidth={false}
                />

                <Button
                  variant="outlined"
                  size="small"
                  onClick={clearFilters}
                  className={classes.clearButton}
                >
                  Clear Filters
                </Button>
              </Box>
            </Box>
          </Box>

          <Box className={classes.transactionsTableContainer}>
              <TableComponent
                data={allTransactions}
                columns={transactionColumns}
                keyField="uuid"
                className="transactions-table"
                totalRecords={totalRecords}
                totalPages={totalPages}
                onChangePagination={(pagination) => setPagination(pagination)}
                remoteSorting
                loading={transactionsLoading}
              />
          </Box>
        </Paper>
      </Box>
        </>
      )}
    </Box>
  )
}

export default Transactions